import type { CreateMigration, MigrationDestination, MigrationInstance, MigrationJob, MigrationRuntimeCategory, MigrationState, MigrationStorage } from '../types/modpackMigration';
import { updaterStateLabel } from './modpackUpdater';

export const MIGRATION_STEPS = ['Inspect', 'Prepare', 'Copy', 'Final sync', 'Verify', 'Connections', 'Accept', 'Clean up'] as const;
export const MIGRATION_EXCLUSIONS = ['logbook', 'logbook-v2', 'logbook-replay', 'snapshots', 'logs', 'crash-reports', 'lootr'] as const;
export const migrationActive = (state: MigrationState) => ['queued', 'running', 'waiting'].includes(state);
export const migrationCanCancel = (state: MigrationState) => migrationActive(state) || state === 'pending_staff_approval';
export const migrationStateLabel = (state: MigrationState) => state === 'pending_staff_approval' ? 'Waiting for Staff Manager approval' : updaterStateLabel(state);
export const migrationBlocksNew = (job: MigrationJob) => migrationCanCancel(job.state) || job.state === 'needs_recovery';
export const migrationOverridePhrase = (machine: string) => `FORCE MEMORY ${machine}`;
export const migrationCancelMode = (job: MigrationJob) => job.state === 'pending_staff_approval' ? 'withdraw'
  : job.can_abort_before_stop === true && !job.stop_authorized_at ? 'abort_before_stop' : 'pause';
export const migrationStorageRequirement = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value)
  ? `${migrationBytes(value)} required` : 'Transfer size calculated during preflight';

/** Durable milestones advance beyond transfer progress without inventing completed work. */
export function migrationProgressStage(job: Pick<MigrationJob, 'phase'>): number | undefined {
  switch (job.phase) {
    case 'destination_ready':
    case 'cutover_approved': return 5;
    case 'cutover': return 6;
    case 'accepted': return 7;
    default: return undefined;
  }
}

export function migrationBytes(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not reported';
  const magnitude = Math.abs(value);
  if (magnitude >= 1024 ** 4) return `${(value / 1024 ** 4).toFixed(2)} TiB`;
  if (magnitude >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GiB`;
  if (magnitude >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MiB`;
  return `${Math.round(value).toLocaleString()} bytes`;
}

export function recommendedMigrationStorage(options: MigrationStorage[]): string {
  return options.find(option => option.recommended && option.sufficient)?.id
    ?? options.find(option => option.sufficient)?.id ?? '';
}

export function migrationSelectionError(instance: MigrationInstance | null, destination: MigrationDestination | undefined,
  storage: string, backupStorage: string, override: boolean, phrase: string, reason: string): string | null {
  if (!instance) return 'Wait for the source and destination inspection.';
  if (!instance.source.agent.online || !instance.source.agent.compatible || !instance.source.instance_id || !instance.source.machine_id)
    return 'The source must report a compatible agent and this exact AMP instance.';
  if (instance.source.blockers.length) return instance.source.blockers[0];
  if (instance.jobs.some(migrationBlocksNew)) return 'Finish or recover the existing migration before starting another.';
  if (!destination) return 'Choose a destination machine.';
  if (destination.machine_id === instance.source.machine_id) return 'Choose a different machine.';
  if (!destination.online || !destination.compatible) return 'The destination needs a compatible online agent.';
  if (destination.blockers.length) return destination.blockers[0];
  const chosen = destination.storage_options.find(option => option.id === storage);
  const backup = destination.backup_storage_options.find(option => option.id === backupStorage);
  if (!chosen || !backup) return 'Choose an inspected instance location and backup location.';
  if (!chosen.sufficient || !backup.sufficient) return 'The selected storage has insufficient space. Memory overrides do not bypass disk requirements.';
  if (!destination.memory || [destination.memory.total_bytes, destination.memory.allocated_bytes, destination.memory.reserved_bytes,
    destination.memory.required_bytes, destination.memory.projected_available_bytes].some(value => typeof value !== 'number' || !Number.isFinite(value)))
    return 'Wait for a complete destination memory budget.';
  if (!destination.memory.sufficient) {
    if (!override) return 'This move exceeds the destination memory budget. Choose another machine or review a memory override.';
    if (phrase !== migrationOverridePhrase(destination.machine_id)) return 'Type the exact destination confirmation to override its memory budget.';
    if (reason.trim().length < 10 || reason.trim().length > 1000 || /[\x00-\x1f\x7f]/.test(reason.trim())) return 'Give a clear reason for the memory override (10 to 1,000 characters).';
  }
  return null;
}

/** Only server-issued option identities cross the API boundary; display paths are never executable input. */
export function migrationCreateBody(instance: MigrationInstance, destination: MigrationDestination, storage: string,
  backupStorage: string, override: boolean, phrase: string, reason: string, runtimeExclusions: readonly MigrationRuntimeCategory[] = MIGRATION_EXCLUSIONS): CreateMigration {
  const error = migrationSelectionError(instance, destination, storage, backupStorage, override, phrase, reason);
  if (error) throw new Error(error);
  if (runtimeExclusions.some(value => !MIGRATION_EXCLUSIONS.includes(value)) || new Set(runtimeExclusions).size !== runtimeExclusions.length)
    throw new Error('Choose only the supported runtime folder categories.');
  return { source_server: instance.source.server_address, destination_machine: destination.machine_id,
    storage_id: storage, backup_storage_id: backupStorage, runtime_exclusions: [...runtimeExclusions].sort(),
    ...(!destination.memory!.sufficient ? { memory_override: reason.trim() } : {}),
  };
}
